import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { resolveTenantProductName } from '../../lib/whiteLabelBranding';
import { fetchReplayLaunchControlsStatus } from '../../lib/replay/launchControls';

interface TourStep {
  title: string;
  text: string;
  content?: React.ReactNode;
  targetHint?: string;
  targetId: string;
  secondaryTargetId?: string;
  additionalTargetIds?: string[];
}

interface OnboardingTourProps {
  theme?: 'light' | 'dark';
  brandId: 'trade-proof' | 'warrior-trading';
  tenantSlug?: string | null;
  primaryColor?: string | null;
  userId: string;
  forceOpen?: boolean;
  autoStart?: boolean;
  onConnectBroker: () => void;
  onNavigate?: (page: string) => void;
  routeKey?: string;
  hasTrades?: boolean;
  onClose?: () => void;
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({
  theme = 'light',
  brandId,
  tenantSlug = null,
  primaryColor = null,
  userId,
  forceOpen = false,
  autoStart = true,
  onConnectBroker,
  onNavigate,
  routeKey,
  hasTrades = false,
  onClose,
}) => {
  const onboardingScope = String(tenantSlug || brandId).trim().toLowerCase() || brandId;
  const storageKey = `onboarding.tour.completed.${onboardingScope}.${userId}`;
  const [isOpen, setIsOpen] = useState(() => {
    if (!autoStart) {
      return false;
    }
    try {
      return localStorage.getItem(storageKey) !== 'true';
    } catch {
      return true;
    }
  });

  const stepStorageKey = `onboarding.tour.step.${onboardingScope}.${userId}`;
  const [currentStep, setCurrentStep] = useState(() => {
    try {
      const saved = Number(sessionStorage.getItem(stepStorageKey));
      return Number.isInteger(saved) && saved >= 0 ? saved : 0;
    } catch {
      return 0;
    }
  });
  const [coords, setCoords] = useState<{ top: number; left: number; position: 'fixed' } | null>(null);
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});
  const cardRef = useRef<HTMLDivElement>(null);

  const isWarrior = brandId === 'warrior-trading';
  const onboardingAccent = /^#[0-9a-f]{6}$/i.test(String(primaryColor || '').trim())
    ? String(primaryColor).trim()
    : '#f59e0b';
  const accentRgb = onboardingAccent.slice(1).match(/../g)?.map((part) => parseInt(part, 16)) || [245, 158, 11];
  const accentLuminance = (0.2126 * accentRgb[0] + 0.7152 * accentRgb[1] + 0.0722 * accentRgb[2]) / 255;
  const onboardingAccentForeground = accentLuminance > 0.58 ? '#18181b' : '#ffffff';
  const onboardingAccentVars = {
    '--onboarding-accent': onboardingAccent,
    '--onboarding-accent-hover': `color-mix(in srgb, ${onboardingAccent} 88%, white)`,
    '--onboarding-accent-foreground': onboardingAccentForeground,
  } as React.CSSProperties;
  const productName = resolveTenantProductName();
  const [replayAvailable, setReplayAvailable] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    fetchReplayLaunchControlsStatus({ signal: controller.signal })
      .then((status) => setReplayAvailable(status?.replayEnabled !== false))
      .catch(() => setReplayAvailable(true));
    return () => controller.abort();
  }, []);

  const steps: TourStep[] = isWarrior
    ? [
        {
          title: 'Welcome to Warrior Trading',
          text: 'Quick tour: here’s where to navigate, import trades, connect your broker, and track your challenge.',
          content: (
            <div className="space-y-6 mt-0.5 text-zinc-700 dark:text-zinc-400">
              <div className="font-serif text-5xl font-normal leading-[0.95] tracking-tight text-zinc-900 dark:text-white">We’ll show<br /><em className="text-zinc-600 dark:text-zinc-500">you</em><br />around.</div>
              <p className="max-w-[280px] text-[15px] font-light leading-8 tracking-wide text-zinc-700 dark:text-zinc-300">In minutes, you’ll learn the layout, import trades, review them, replay the market, and journal insights.</p>
              <p className="max-w-[280px] border-t border-zinc-300 dark:border-zinc-800 pt-4 text-sm font-normal leading-7 text-zinc-700 dark:text-zinc-300">When you’re finished, you’ll be ready to use the platform on your own.</p>
            </div>
          ),
          targetHint: 'Dashboard Overview',
          targetId: '',
        },
        {
          title: 'Top navigation',
          text: 'Use the top menu to move between the main areas of Warrior Trading.',
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Use the top menu to move between the main areas of Warrior Trading.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Dashboard</strong> — View your performance snapshot, challenge progress, stats, and saved dashboard layouts.</li>
                <li><strong>Calendar</strong> — Review your trading activity by day and spot active or inactive trading periods.</li>
                <li><strong>Trends</strong> — Find patterns in your trading behavior, performance, mistakes, and consistency.</li>
                <li><strong>Trades</strong> — Browse, filter, and inspect individual trades.</li>
                <li><strong>Journal</strong> — Add notes, review execution, and track what you learned.</li>
                <li><strong>New Trade</strong> — Manually add a trade when you need to enter one yourself.</li>
              </ul>
            </div>
          ),
          targetHint: 'Navigation Menu',
          targetId: 'top-navigation',
        },
        {
          title: 'Choose your navigation layout',
          text: 'Trade-Proof supports Top Navigation and Side Navigation. Your organization sets the default experience, and you can switch layouts anytime for your personal preference.',
          targetHint: 'Navigation layout toggle',
          targetId: 'navigation-layout-toggle',
        },
        {
          title: 'Customize your dashboard',
          text: 'Shape the dashboard around the way you work. Edit the grid first, choose the information you want to see, then save your layout.',
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Shape the dashboard around the way you work.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Edit Grid</strong> — Choose how many columns of widgets you want on screen. The available options automatically adjust to your screen size.</li>
                <li><strong>Manage Widgets</strong> — Choose which information appears on your dashboard.</li>
                <li><strong>Save</strong> — Save your changes to the current dashboard layout.</li>
                <li><strong>Preview Default</strong> — Compare your customized dashboard with its original layout.</li>
              </ul>
            </div>
          ),
          targetHint: 'Dashboard Customization',
          targetId: 'dashboard-customization-controls',
        },
        {
          title: 'Dashboard presets',
          text: 'Use presets to switch what your dashboard is focused on.',
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Use presets to switch what your dashboard is focused on.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Performance</strong> — See the big picture: P&L, win rate, drawdown, and the key numbers that show how you're performing.</li>
                <li><strong>Trades</strong> — Focus on your individual trades and recent trading activity.</li>
                <li><strong>Patterns</strong> — Look beyond individual trades to spot recurring setups, behaviors, mistakes, and consistency.</li>
                <li><strong>Complete</strong> — Bring everything together in one comprehensive dashboard view.</li>
              </ul>
            </div>
          ),
          targetHint: 'Dashboard Presets Menu',
          targetId: 'dashboard-preset-list',
        },
        {
          title: 'User menu',
          text: 'Use this menu for account actions, imports, support, and app preferences.',
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Use this menu for account actions, imports, support, and app preferences.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Import Trades</strong> — Import or sync trades into your account.</li>
                <li><strong>Settings</strong> — Manage broker connection, charting setup, account details, and preferences.</li>
                <li><strong>Challenges</strong> — Open challenge tracking and review your challenge progress.</li>
                <li><strong>Support</strong> — Get help or contact support.</li>
                <li><strong>Light mode / Dark mode</strong> — Switch the app appearance.</li>
                <li><strong>Logout</strong> — Sign out of your account.</li>
              </ul>
            </div>
          ),
          targetHint: 'User Actions Menu',
          targetId: 'user-menu-button',
        },
        {
          title: 'Find the trade you want to review',
          text: 'Open Trades to browse your imported and manually entered trades. Use the available search and filters to narrow the list, then open a trade for its full review.',
          targetId: 'trades-page',
          targetHint: 'Trades navigation',
          secondaryTargetId: 'top-nav-trades',
        },
        {
          title: 'Understand what happened in the trade',
          text: 'Trade Details brings together the trade’s entry, exit, P&L, execution information, notes, tags, and available chart context so you can review the decision as a whole.',
          targetHint: 'Trade Details',
          targetId: 'trade-details',
        },
        {
          title: 'Reflect in your Journal',
          text: 'Use Journal alongside Trades, Trade Details, and Replay to record decisions, mistakes, lessons, and patterns you want to carry into future trades.',
          targetHint: 'Journal',
          targetId: 'journal-page',
          secondaryTargetId: 'top-nav-journal',
        },
        {
          title: 'You’re ready to start reviewing your trades',
          text: 'Import your trades to begin reviewing your performance, inspecting individual executions, revisiting the market with Replay, and recording what you learn in Journal.',
          targetHint: 'Trades navigation',
          targetId: '',
        },
      ]
    : [
        {
          title: `Welcome to ${productName}`,
          text: 'Quick tour: here’s where to navigate, import trades, connect your broker, and review your journal.',
          content: (
            <div className="space-y-6 mt-0.5 text-zinc-700 dark:text-zinc-400">
              <div className="font-serif text-5xl font-normal leading-[0.95] tracking-tight text-zinc-900 dark:text-white">We’ll show<br /><em className="text-zinc-600 dark:text-zinc-500">you</em><br />around.</div>
              <p className="max-w-[280px] text-[15px] font-light leading-8 tracking-wide text-zinc-700 dark:text-zinc-300">In minutes, you’ll learn the layout, import trades, review them, replay the market, and journal insights.</p>
              <p className="max-w-[280px] border-t border-zinc-300 dark:border-zinc-800 pt-4 text-sm font-normal leading-7 text-zinc-700 dark:text-zinc-300">When you’re finished, you’ll be ready to use the platform on your own.</p>
            </div>
          ),
          targetHint: 'Dashboard Overview',
          targetId: '',
        },
        {
          title: 'Top navigation',
          text: `Use the top menu to move between the main areas of ${productName}.`,
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Use the top menu to move between the main areas of {productName}.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Dashboard</strong> — View your performance snapshot, stats, and saved dashboard layouts.</li>
                <li><strong>Calendar</strong> — Review your trading activity by day and spot active or inactive trading periods.</li>
                <li><strong>Trends</strong> — Find patterns in your trading behavior, performance, mistakes, and consistency.</li>
                <li><strong>Trades</strong> — Browse, filter, and inspect individual trades.</li>
                <li><strong>Journal</strong> — Add notes, review execution, and track what you learned.</li>
                <li><strong>New Trade</strong> — Manually add a trade when you need to enter one yourself.</li>
              </ul>
            </div>
          ),
          targetHint: 'Navigation Menu',
          targetId: 'top-navigation',
        },
        {
          title: 'Choose your navigation layout',
          text: 'Trade-Proof supports Top Navigation and Side Navigation. Your organization sets the default experience, and you can switch layouts anytime for your personal preference.',
          targetHint: 'Navigation layout toggle',
          targetId: 'navigation-layout-toggle',
        },
        {
          title: 'Customize your dashboard',
          text: 'Shape the dashboard around the way you work. Edit the grid first, choose the information you want to see, then save your layout.',
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Shape the dashboard around the way you work.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Edit Grid</strong> — Choose how many columns of widgets you want on screen. The available options automatically adjust to your screen size.</li>
                <li><strong>Manage Widgets</strong> — Choose which information appears on your dashboard.</li>
                <li><strong>Save</strong> — Save your changes to the current dashboard layout.</li>
                <li><strong>Preview Default</strong> — Compare your customized dashboard with its original layout.</li>
              </ul>
            </div>
          ),
          targetHint: 'Dashboard Customization',
          targetId: 'dashboard-customization-controls',
        },
        {
          title: 'Dashboard presets',
          text: 'Use presets to switch what your dashboard is focused on.',
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Use presets to switch what your dashboard is focused on.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Performance</strong> — See the big picture: P&L, win rate, drawdown, and the key numbers that show how you're performing.</li>
                <li><strong>Trades</strong> — Focus on your individual trades and recent trading activity.</li>
                <li><strong>Patterns</strong> — Look beyond individual trades to spot recurring setups, behaviors, mistakes, and consistency.</li>
                <li><strong>Complete</strong> — Bring everything together in one comprehensive dashboard view.</li>
              </ul>
            </div>
          ),
          targetHint: 'Dashboard Presets Menu',
          targetId: 'dashboard-preset-list',
        },
        {
          title: 'User menu',
          text: 'Use this menu for account actions, imports, support, and app preferences.',
          content: (
            <div className="space-y-2 mt-0.5 text-zinc-700 dark:text-zinc-400 [&_strong]:text-zinc-950 dark:[&_strong]:text-white">
              <p className="text-sm leading-relaxed">Use this menu for account actions, imports, support, and app preferences.</p>
              <ul className="list-disc pl-4 space-y-2 text-sm leading-relaxed">
                <li><strong>Import Trades</strong> — Import or sync trades into your account.</li>
                <li><strong>Settings</strong> — Manage broker connection, charting setup, account details, and preferences.</li>
                <li><strong>Support</strong> — Get help or contact support.</li>
                <li><strong>Light mode / Dark mode</strong> — Switch the app appearance.</li>
                <li><strong>Logout</strong> — Sign out of your account.</li>
              </ul>
            </div>
          ),
          targetHint: 'User Actions Menu',
          targetId: 'user-menu-button',
        },
        {
          title: 'Find the trade you want to review',
          text: 'Open Trades to browse your imported and manually entered trades. Use the available search and filters to narrow the list, then open a trade for its full review.',
          targetId: 'trades-page',
          targetHint: 'Trades navigation',
          secondaryTargetId: 'top-nav-trades',
        },
        {
          title: 'Understand what happened in the trade',
          text: 'Trade Details brings together the trade’s entry, exit, P&L, execution information, notes, tags, and available chart context so you can review the decision as a whole.',
          targetHint: 'Trade Details',
          targetId: 'trade-details',
        },
        {
          title: 'Reflect in your Journal',
          text: 'Use Journal alongside Trades, Trade Details, and Replay to record decisions, mistakes, lessons, and patterns you want to carry into future trades.',
          targetHint: 'Journal',
          targetId: 'journal-page',
          secondaryTargetId: 'top-nav-journal',
        },
        {
          title: 'You’re ready to start reviewing your trades',
          text: 'Import your trades to begin reviewing your performance, inspecting individual executions, revisiting the market with Replay, and recording what you learn in Journal.',
          targetHint: 'Trades navigation',
          targetId: '',
        },
      ];

  const filteredSteps = steps.filter((step) =>
    (hasTrades || (step.targetId !== 'trade-details' && step.targetId !== 'trade-replay'))
    && (replayAvailable || step.targetId !== 'trade-replay')
  );
  const totalSteps = filteredSteps.length;
  const safeCurrentStep = Math.min(currentStep, Math.max(0, totalSteps - 1));
  const stepInfo = filteredSteps[safeCurrentStep];
  useEffect(() => {
    if (currentStep !== safeCurrentStep) setCurrentStep(safeCurrentStep);
  }, [currentStep, safeCurrentStep]);

  const handleComplete = () => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (e) {
      console.warn('Failed to save onboarding tour state:', e);
    }
    try { sessionStorage.removeItem(stepStorageKey); } catch {}
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleNext = () => {
    if (safeCurrentStep < totalSteps - 1) {
      const nextStep = safeCurrentStep + 1;
      try { sessionStorage.setItem(stepStorageKey, String(nextStep)); } catch {}
      setCurrentStep(nextStep);
      const nextTarget = filteredSteps[nextStep]?.targetId;
      if (nextTarget === 'trade-details') {
        try { (window as any).__TT_reviewOnboardingTrade?.(); } catch {}
      }
      const nextRoute = nextTarget === 'trades-page'
        ? 'Trades'
        : nextTarget === 'trade-details' || nextTarget === 'trade-replay'
          ? 'Trade Details'
          : nextTarget === 'journal-page' ? 'Journal' : null;
      if (nextTarget === 'journal-page') {
        (window as any).__TT_onboardingJournalYtd = true;
        setTimeout(() => window.dispatchEvent(new CustomEvent('tradeproof:onboarding-journal-ytd')), 0);
      }
      if (nextRoute) onNavigate?.(nextRoute);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (safeCurrentStep > 0) {
      const previousStep = safeCurrentStep - 1;
      try { sessionStorage.setItem(stepStorageKey, String(previousStep)); } catch {}
      setCurrentStep(previousStep);
      const previousTarget = filteredSteps[previousStep]?.targetId;
      const previousRoute = previousTarget === 'journal-page' ? 'Journal' : previousTarget === 'trades-page' ? 'Trades' : previousTarget === 'trade-details' || previousTarget === 'trade-replay' ? 'Trade Details' : 'Dashboard';
      if (previousTarget === 'journal-page') {
        (window as any).__TT_onboardingJournalYtd = true;
        setTimeout(() => window.dispatchEvent(new CustomEvent('tradeproof:onboarding-journal-ytd')), 0);
      }
      onNavigate?.(previousRoute);
    }
  };

  useEffect(() => {
    const restart = () => {
      try { localStorage.removeItem(storageKey); sessionStorage.removeItem(stepStorageKey); } catch {}
      setCurrentStep(0);
      setIsOpen(true);
    };
    window.addEventListener('tradeproof:restart-product-tour', restart);
    return () => window.removeEventListener('tradeproof:restart-product-tour', restart);
  }, [storageKey, stepStorageKey]);

  // Handle forceOpen triggers from parent Replay/Reset controls
  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      setCurrentStep(0);
    }
  }, [forceOpen]);

  // Inject visual styles for highlighted elements & overlay once
  useEffect(() => {
    const styleId = 'onboarding-tour-custom-styles';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.innerHTML = `
        .tour-highlight-amber {
          position: relative !important;
          outline: 4px solid #d97706 !important;
          outline-offset: 4px !important;
          border-radius: 12px !important;
          box-shadow: 0 0 25px rgba(217, 119, 6, 0.6) !important;
          z-index: 1001 !important;
          transition: all 0.3s ease-in-out;
        }
        .tour-dimmed-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(9, 9, 11, 0.45);
          backdrop-filter: blur(4px);
          z-index: 1000;
          pointer-events: none;
          animation: tourFadeIn 0.25s ease-out;
          transition: clip-path 0.25s ease-in-out;
        }
        .tour-dimmed-overlay.tour-welcome-backdrop {
          background-color: rgba(9, 9, 11, 0.7);
          backdrop-filter: blur(4px);
          z-index: 10000;
        }
        @keyframes tourFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `;
      document.head.appendChild(styleEl);
    }
    return () => {
      document.getElementById(styleId)?.remove();
    };
  }, []);

  // Trigger open/close of menus based on current step
  useEffect(() => {
    if (!isOpen) return;

    const currentStepTarget = stepInfo.targetId;

    // 1. Dashboard preset menu handling
    if (currentStepTarget === 'dashboard-preset-list' || currentStepTarget === 'dashboard-customization-controls' || currentStepTarget === 'dashboard-edit-grid') {
      window.dispatchEvent(new CustomEvent('dashboard:open-preset-menu'));
    } else {
      window.dispatchEvent(new CustomEvent('dashboard:close-preset-menu'));
    }

    // 2. User actions menu handling
    const needsUserMenuOpen =
      currentStepTarget === 'user-menu-button' ||
      currentStepTarget === 'user-menu-import-trades' ||
      currentStepTarget === 'user-menu-settings' ||
      currentStepTarget === 'user-menu-challenges' ||
      currentStepTarget === 'user-menu-support';

    if (needsUserMenuOpen) {
      window.dispatchEvent(new CustomEvent('dashboard:open-user-menu'));
    } else {
      window.dispatchEvent(new CustomEvent('dashboard:close-user-menu'));
    }
  }, [currentStep, isOpen, stepInfo.targetId]);

  // Target finding and popover positioning
  useEffect(() => {
    if (!isOpen) return;

    const targetId = stepInfo.targetId;
    const secondaryTargetId = stepInfo.secondaryTargetId;
    const additionalTargetIds = stepInfo.additionalTargetIds || [];
    let targetEl: Element | null = null;
    let secondaryTargetEl: Element | null = null;
    let additionalTargetElements: Element[] = [];
    let cancelled = false;

    const clearHighlights = () => {
      document.querySelectorAll('.tour-highlight-amber, .tour-highlight-blue').forEach((element) => {
        element.classList.remove('tour-highlight-amber', 'tour-highlight-blue');
      });
    };

    // Ownership starts clean for every step. This also removes a highlight
    // left by a route that is about to unmount.
    clearHighlights();

    const findVisibleElement = (id: string) => {
      if (!id) return null;
      const elements = document.querySelectorAll(`[data-tour-target="${id}"]`);
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];
        const style = window.getComputedStyle(el);
        if (style.display !== 'none' && style.visibility !== 'hidden' && (el as HTMLElement).offsetWidth > 0) {
          return el;
        }
      }
      return null;
    };

    const resolveAndHighlightTargets = () => {
      targetEl = findVisibleElement(targetId);
      secondaryTargetEl = findVisibleElement(secondaryTargetId || '');
      additionalTargetElements = additionalTargetIds
        .map((id) => findVisibleElement(id))
        .filter((element): element is Element => element !== null);

      if (targetEl && !isWelcomeStep && !cancelled) {
        targetEl.classList.add('tour-highlight-amber');
        if (typeof targetEl.scrollIntoView === 'function') {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      if (secondaryTargetEl && !isWelcomeStep && !cancelled && secondaryTargetEl !== targetEl) {
        secondaryTargetEl.classList.add('tour-highlight-amber');
      }
      if (!isWelcomeStep && !cancelled) {
        additionalTargetElements.forEach((element) => {
          if (element !== targetEl && element !== secondaryTargetEl) {
            element.classList.add('tour-highlight-amber');
          }
        });
      }
    };

    resolveAndHighlightTargets();

    const updatePosition = () => {
      if (isWelcomeStep && targetEl) {
        const card = cardRef.current?.getBoundingClientRect();
        const cardWidth = card?.width || 400;
        const cardHeight = card?.height || 520;
        setCoords({
          top: Math.max(12, (window.innerHeight - cardHeight) / 2),
          left: Math.max(12, (window.innerWidth - cardWidth) / 2),
          position: 'fixed',
        });
        setOverlayStyle({
          clipPath: `polygon(
            0px 0px,
            0px 100vh,
            100vw 100vh,
            100vw 0px,
            0px 0px
          )`
        });
        return;
      }

      if (!targetEl) {
        setCoords(null); // Fallback to centered modal
        setOverlayStyle({
          clipPath: `polygon(
            0px 0px,
            0px 100vh,
            100vw 100vh,
            100vw 0px,
            0px 0px,
            0px 0px,
            0px 0px,
            0px 0px,
            0px 0px,
            0px 0px
          )`
        });
        return;
      }

      const rect = targetEl.getBoundingClientRect();
      const card = cardRef.current?.getBoundingClientRect();
      const cardWidth = card?.width || Math.min(760, Math.max(320, window.innerWidth - 40));
      const cardHeight = card?.height || Math.min(520, Math.max(320, window.innerHeight - 120));
      const viewportPadding = 12;
      const gap = 24;
      const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

      // Prefer a position with a clear gap from the highlighted target. Candidates
      // are ordered by the side with the most usable room, then by proximity.
      const candidatePositions = targetId === 'top-navigation'
        ? [
            { top: window.innerHeight / 2 - cardHeight / 2, left: window.innerWidth / 2 - cardWidth / 2 },
          ]
        : targetId === 'user-menu-button'
        ? [
            { top: rect.top + rect.height / 2 - cardHeight / 2, left: rect.left - cardWidth - gap - 300 },
            { top: rect.bottom + gap, left: rect.left + rect.width / 2 - cardWidth / 2 },
            { top: rect.top - cardHeight - gap, left: rect.left + rect.width / 2 - cardWidth / 2 },
            { top: rect.top + rect.height / 2 - cardHeight / 2, left: rect.right + gap },
          ]
        : targetId === 'dashboard-preset-list' || targetId === 'dashboard-customization-controls' || targetId === 'dashboard-edit-grid'
        ? [
            { top: rect.top + rect.height / 2 - cardHeight / 2, left: rect.left - cardWidth - gap - 200 },
            { top: rect.bottom + gap, left: rect.left + rect.width / 2 - cardWidth / 2 },
            { top: rect.top - cardHeight - gap, left: rect.left + rect.width / 2 - cardWidth / 2 },
            { top: rect.top + rect.height / 2 - cardHeight / 2, left: rect.right + gap },
          ]
        : [
            { top: rect.top + rect.height / 2 - cardHeight / 2, left: rect.right + gap },
            { top: rect.top + rect.height / 2 - cardHeight / 2, left: rect.left - cardWidth - gap },
            { top: rect.bottom + gap, left: rect.left + rect.width / 2 - cardWidth / 2 },
            { top: rect.top - cardHeight - gap, left: rect.left + rect.width / 2 - cardWidth / 2 },
          ];
      const candidates = candidatePositions.map((candidate) => ({
        top: clamp(candidate.top, viewportPadding, window.innerHeight - cardHeight - viewportPadding),
        left: clamp(candidate.left, viewportPadding, window.innerWidth - cardWidth - viewportPadding),
      }));

      const fitsWithoutOverlap = (candidate: { top: number; left: number }) => {
        const separatedHorizontally = candidate.left + cardWidth <= rect.left - gap || candidate.left >= rect.right + gap;
        const separatedVertically = candidate.top + cardHeight <= rect.top - gap || candidate.top >= rect.bottom + gap;
        return separatedHorizontally || separatedVertically;
      };

      const centered = {
        top: clamp(window.innerHeight / 2 - cardHeight / 2, viewportPadding, window.innerHeight - cardHeight - viewportPadding),
        left: clamp(window.innerWidth / 2 - cardWidth / 2, viewportPadding, window.innerWidth - cardWidth - viewportPadding),
      };
      const chosen = candidates.find(fitsWithoutOverlap) || centered;
      const { top, left } = chosen;

      // Calculate cutout coords
      const x1 = rect.left - 6;
      const y1 = rect.top - 6;
      const x2 = rect.right + 6;
      const y2 = rect.bottom + 6;

      setOverlayStyle({
        clipPath: `polygon(
          0px 0px,
          0px 100vh,
          100vw 100vh,
          100vw 0px,
          0px 0px,
          ${x1}px ${y1}px,
          ${x2}px ${y1}px,
          ${x2}px ${y2}px,
          ${x1}px ${y2}px,
          ${x1}px ${y1}px
        )`
      });

      setCoords({ top, left, position: 'fixed' });
    };

    // A route or menu can mount its exact target on the next paint. Retry only
    // the declared target and cancel this work as soon as the step changes.
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const resolveAndPosition = () => {
      if (cancelled) return;
      clearHighlights();
      resolveAndHighlightTargets();
      if (targetId && !targetEl && attempts < 8) {
        attempts += 1;
        retryTimer = setTimeout(resolveAndPosition, 16);
        return;
      }
      updatePosition();
    };
    const timer = setTimeout(resolveAndPosition, 0);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timer);
      if (retryTimer) clearTimeout(retryTimer);
      cancelled = true;
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      clearHighlights();
    };
  }, [currentStep, isOpen, stepInfo.targetId, stepInfo.secondaryTargetId, stepInfo.additionalTargetIds?.join('|'), isWarrior, routeKey]);

  // Keyboard navigation accessibility
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleComplete();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentStep]);

  if (!isOpen) {
    return null;
  }

  const isDark = theme === 'dark';
  const isWelcomeStep = safeCurrentStep === 0;

  const innerPopover = (
    <div
      ref={cardRef}
      className={`w-full overflow-hidden border p-5 shadow-2xl transition-all duration-300 relative ${isWelcomeStep ? 'mx-auto max-w-[400px] h-auto min-h-0 rounded-lg p-8' : 'rounded-lg p-8'} ${
        coords ? (stepInfo.targetId === 'user-menu-button'
          ? 'w-[min(720px,calc(100vw-40px))] max-w-[min(720px,calc(100vw-40px))]'
          : stepInfo.targetId === 'dashboard-preset-list'
            ? 'w-[min(820px,calc(100vw-40px))] max-w-[min(820px,calc(100vw-40px))]'
            : stepInfo.targetId === 'dashboard-customization-controls'
              ? 'w-[min(760px,calc(100vw-40px))] max-w-[min(760px,calc(100vw-40px))]'
            : stepInfo.targetId === 'trade-details'
              ? 'w-[min(600px,calc(100vw-40px))] max-w-[min(600px,calc(100vw-40px))]'
            : stepInfo.targetId === 'journal-page'
              ? 'w-[min(532px,calc(100vw-40px))] max-w-[min(532px,calc(100vw-40px))]'
            : 'w-[min(760px,calc(100vw-40px))] max-w-[min(760px,calc(100vw-40px))]') + ' border-zinc-200 dark:border-zinc-800' : (isWelcomeStep ? 'max-w-[400px]' : 'max-w-lg') + ' md:p-8 border-zinc-200 dark:border-zinc-800'
      } ${
        isDark
          ? 'bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 text-white'
          : 'bg-gradient-to-br from-white via-slate-50 to-slate-100 text-zinc-900'
      }`}
      style={{
        filter: 'none',
        backdropFilter: 'none',
        pointerEvents: 'auto',
        ...onboardingAccentVars,
      }}
      data-testid="onboarding-tour-dialog"
    >
      {/* Decorative accent element */}
      <div
        className="absolute -right-16 -top-16 h-36 w-36 rounded-full blur-[80px] pointer-events-none"
        style={{ backgroundColor: onboardingAccent, opacity: 0.15 }}
      />

      {/* Progress indicator bar */}
      <div className="absolute top-0 left-0 right-0 flex h-1 gap-1 bg-zinc-800/40 dark:bg-zinc-200/20">
        {Array.from({ length: totalSteps }, (_, stepIndex) => (
          <div
            key={stepIndex}
            className={`h-full flex-1 transition-colors duration-300 ${
              stepIndex <= currentStep
                ? 'bg-[var(--onboarding-accent)]'
                : isWelcomeStep ? 'bg-zinc-800' : 'bg-zinc-800/20 dark:bg-zinc-200/20'
            }`}
            aria-hidden="true"
          />
        ))}
      </div>

      <span className="absolute right-0 top-0 rounded-bl-md bg-[var(--onboarding-accent)] px-5 py-1.5 font-sans text-sm font-normal lowercase tracking-normal text-[var(--onboarding-accent-foreground)]">step {currentStep + 1}/{totalSteps}<span className="sr-only">STEP {String(currentStep + 1).padStart(2, '0')} / {String(totalSteps).padStart(2, '0')}</span></span>

      {/* Header section */}
      <div className={`relative flex ${isWelcomeStep ? 'flex-col items-center mb-0 gap-3 text-center' : 'justify-between items-center mb-4'}`}>
      </div>

      {/* Content Section */}
      <div className={`pr-1 ${isWelcomeStep ? '' : 'space-y-3 mb-6 min-h-[100px]'}`}>
        <h2
          id="tour-title"
          className={isWelcomeStep ? 'mb-8 w-full max-w-full min-w-0 font-serif text-5xl font-normal leading-[0.95] tracking-tight text-zinc-900 dark:text-white' : 'mb-6 w-full border-b border-zinc-300 dark:border-zinc-700 pb-3 font-serif text-3xl font-normal leading-tight tracking-tight text-[var(--onboarding-accent)]'}
        >
          {isWelcomeStep ? <><span className="mb-2 block font-sans text-[10px] font-medium uppercase tracking-[0.22em] text-zinc-100">Welcome to</span><span className="mt-2 block max-w-full whitespace-normal break-words border-b-[0.5px] border-zinc-300 dark:border-zinc-700 pb-2 font-serif text-[clamp(2rem,4vw,2.25rem)] leading-tight text-[var(--onboarding-accent)]" style={{ color: 'var(--onboarding-accent)' }}>{isWarrior ? 'Warrior Trading' : productName}</span></> : stepInfo.title}
        </h2>
        {stepInfo.content ? (
          stepInfo.content
        ) : (
          <p className={`text-sm font-normal leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-700'}`}>
            {stepInfo.text}
          </p>
        )}



        {safeCurrentStep === totalSteps - 1 && (
          <div className="pt-1">
            <button
              onClick={() => { onNavigate?.('Import Trades'); handleComplete(); }}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-bold rounded-lg transition-all duration-200 hover:scale-[1.02] ${
                'bg-[var(--onboarding-accent)] text-[var(--onboarding-accent-foreground)] hover:bg-[var(--onboarding-accent-hover)]'
              }`}
              data-testid="tour-final-cta"
            >
              Import Trades
              <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Footer Controls Bar */}
      {isWelcomeStep && <button onClick={handleNext} className="group mb-2 mt-8 flex w-[220px] max-w-full items-center justify-center gap-2 rounded-full bg-[var(--onboarding-accent)] px-5 py-2 text-center text-sm font-medium text-[var(--onboarding-accent-foreground)] transition-transform hover:bg-[var(--onboarding-accent-hover)] hover:translate-x-0.5" data-testid="tour-next-btn"><span>Start Tour</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>}
      <div className={`${isWelcomeStep ? 'hidden' : 'flex'} items-center pt-4 border-t border-zinc-300 dark:border-zinc-700/70 ${currentStep === 0 ? 'justify-center' : 'justify-between'}`}>
        {currentStep > 0 ? (
          <button
            onClick={handleComplete}
            className={`text-sm font-semibold hover:underline transition-all ${
              isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-600 hover:text-zinc-900'
            }`}
            data-testid="tour-skip-btn"
          >
            Skip Tour
          </button>
        ) : null}

        <div className="flex items-center gap-1.5">
          {currentStep > 0 ? <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-bold rounded-lg transition-all border ${currentStep === 0 ? 'hidden' : ''} ${
              currentStep === 0
                ? 'opacity-100 cursor-not-allowed border-zinc-800/10 text-zinc-400 dark:border-zinc-200/10'
                : isDark
                ? 'border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-white'
                : 'border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
            }`}
            data-testid="tour-prev-btn"
          >
            <ArrowLeft className="h-3 w-3" />
            Prev
          </button> : null}

          <button
            onClick={handleNext}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-bold rounded-lg transition-all hover:scale-[1.01] ${
              'bg-[var(--onboarding-accent)] text-[var(--onboarding-accent-foreground)] hover:bg-[var(--onboarding-accent-hover)]'
            }`}
            data-testid="tour-next-btn"
          >
            {currentStep === 0 ? 'START TOUR' : currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
            {currentStep > 0 ? <ArrowRight className="h-3 w-3" /> : null}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Visual background overlay (click-through friendly) */}
      <div
        className={`tour-dimmed-overlay ${isWelcomeStep ? 'tour-welcome-backdrop' : ''}`}
        style={{ ...overlayStyle, clipPath: 'none' }}
        aria-hidden="true"
      />

      <div
        style={coords
          ? stepInfo.targetId === 'top-navigation'
            ? { position: coords.position, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: isWelcomeStep ? 10001 : 1002 }
            : { position: coords.position, top: `${coords.top}px`, left: `${coords.left}px`, zIndex: isWelcomeStep ? 10001 : 1002 }
          : { zIndex: isWelcomeStep ? 10001 : 1002 }}
        className={coords
          ? 'animate-in zoom-in-95 duration-200'
          : 'fixed inset-0 flex items-center justify-center p-4 bg-transparent animate-in fade-in duration-200'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        data-testid="onboarding-tour-overlay"
      >
        {innerPopover}
      </div>
    </>
  );
};

export default OnboardingTour;
